// BSC Types for frontend

// Measurement direction for KRAs
// 'maximize' = higher is better (sales, revenue) - formula: (actual/target) * 100
// 'minimize' = lower is better (costs, errors) - formula: (target/actual) * 100
export type MeasurementDirection = 'maximize' | 'minimize';

export interface BSCKRAScore {
  objective: string;
  measure: string;
  weight: number;
  unitOfMeasure: string;
  measurementDirection?: MeasurementDirection; // 'maximize' (higher is better) or 'minimize' (lower is better)
  isExtraDuty?: boolean; // Flag for extra duties beyond normal job scope
  targetValue?: number;
  targetDescription?: string;
  actualValue?: number;
  actualDescription?: string;
  performance?: number;
  score?: number;
  weightedScore?: number;
  managerComments?: string;
  managerAdjustedScore?: number;
}

export interface BSCPerspectiveScore {
  name: string;
  weight: number;
  kraScores: BSCKRAScore[];
  perspectiveTotal?: number;
}

export interface BalancedScorecard {
  id?: string;
  reviewPeriod: string;
  periodType: string;
  employeeId?: string;
  employeeName?: string;
  jobDescriptionId?: string;
  jobDescriptionTitle?: string;
  perspectives: BSCPerspectiveScore[];
  totalWeightedScore?: number;
  rawTotalWeightedScore?: number; // Uncapped raw score (only set if exceeded 110%)
  overallPerformance?: number;
  finalGrade?: string;
  bonusEntitlement?: number;
  status: string;
  employeeComments?: string;
  managerId?: string;
  managerName?: string;
  managerComments?: string;
  managerAdjustedScore?: number; // Manager can override total weighted score
  managerAdjustmentNotes?: string; // Justification for adjustment
  submittedAt?: string;
  reviewedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface PerformanceRating {
  grade: string;
  minPercentage: number;
  maxPercentage: number;
  bonusEntitlement: number;
  description?: string;
  colorCode?: string;
}

// ===== Qualification Types =====
export interface Qualification {
  name: string;
  level?: string; // e.g., "Bachelor's Degree", "Master's Degree", "Diploma"
  field?: string; // e.g., "Computer Science", "Business Administration"
  isRequired: boolean;
}

export interface ProfessionalRegistration {
  body: string; // e.g., "CIMA", "ACCA", "PMP"
  membershipType?: string; // e.g., "Full Member", "Associate"
  isRequired: boolean;
}

export interface AuthorityLimit {
  category: string; // e.g., "Financial", "Procurement", "HR/Personnel", "Operational", "Legal/Contracts", "IT/Technology"
  description: string;
  // Financial & Procurement specific
  monetaryLimit?: number;
  currency?: string;
  // HR/Personnel specific
  headcountLimit?: number;
  salaryBandLimit?: string;
  // Operational specific
  unitLimit?: number;
  unitType?: string; // e.g., "units", "hours", "days", "projects"
  // Legal/Contracts specific
  contractDurationLimit?: string;
  contractValueLimit?: number;
  // IT/Technology specific
  accessLevel?: string; // e.g., "Read", "Write", "Admin", "Full"
  systemScope?: string;
  // Common fields
  approvalRequired?: string; // Who needs to approve beyond this limit
}

export interface KeyResultArea {
  description: string;
}

export interface BSCPerspective {
  name: string;
  kras: string[]; // Simple list of KRA descriptions
}

export interface JobDescription {
  id?: string;
  // Job Description Section
  title: string;
  grade?: string;
  department?: string;
  businessUnit?: string; // Business unit this job description belongs to
  jobPurpose?: string;
  
  // Accountability Section
  reportsTo?: string; // Job title of supervisor (denormalized)
  reportsToJobDescriptionId?: string; // ID of the supervisor's JD
  reportsToUserId?: string; // User ID of supervisor (from supervisor JD assignment)
  directReportsCount?: number;
  indirectReportsCount?: number;
  limitsToAuthority?: AuthorityLimit[];
  
  // Inherent Job Requirements Section
  minimumQualifications?: Qualification[];
  minimumJobKnowledge?: string[];
  minimumExperienceYears?: string;
  minimumExperienceDescription?: string;
  professionalRegistrations?: ProfessionalRegistration[];
  otherRequirements?: string[];
  
  // BSC/Performance Section
  bscPerspectives: BSCPerspective[];
  requiredCompetencies?: string[];
  
  // User Assignment
  assignedUserId?: string;
  assignedUserName?: string;
  createdByUserId?: string;
  createdByName?: string;
  
  // Metadata
  isActive: boolean;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Org Chart Types
export interface OrgChartNode {
  user: {
    id: string;
    name?: string;
    email: string;
    jobTitle?: string;
    department?: string;
  };
  directReports: OrgChartNode[];
  directReportsCount: number;
  indirectReportsCount: number;
}

export interface ReportsCounts {
  directReportsCount: number;
  indirectReportsCount: number;
}

// Default BSC Perspectives
export const DEFAULT_BSC_PERSPECTIVES: BSCPerspectiveScore[] = [
  {
    name: 'Financial',
    weight: 25,
    kraScores: [],
  },
  {
    name: 'Customer',
    weight: 25,
    kraScores: [],
  },
  {
    name: 'Internal Processes',
    weight: 25,
    kraScores: [],
  },
  {
    name: 'Learning & Growth',
    weight: 25,
    kraScores: [],
  },
];

// Default Performance Ratings (for grades only)
export const PERFORMANCE_RATINGS: PerformanceRating[] = [
  {grade: 'A+', minPercentage: 120, maxPercentage: 999, bonusEntitlement: 100, description: 'Exceptional - Far Exceeds Expectations', colorCode: '#033e79'},
  {grade: 'A', minPercentage: 110, maxPercentage: 119.99, bonusEntitlement: 100, description: 'Outstanding - Exceeds Expectations', colorCode: '#033e79'},
  {grade: 'A-', minPercentage: 105, maxPercentage: 109.99, bonusEntitlement: 75, description: 'Excellent', colorCode: '#17A2B8'},
  {grade: 'B+', minPercentage: 100, maxPercentage: 104.99, bonusEntitlement: 75, description: 'Very Good - Meets All Expectations', colorCode: '#17A2B8'},
  {grade: 'B', minPercentage: 95, maxPercentage: 99.99, bonusEntitlement: 50, description: 'Good - Meets Most Expectations', colorCode: '#17A2B8'},
  {grade: 'C', minPercentage: 89.5, maxPercentage: 94.99, bonusEntitlement: 50, description: 'Satisfactory - Meets Some Expectations', colorCode: '#32CD32'},
  {grade: 'C-', minPercentage: 85, maxPercentage: 89.49, bonusEntitlement: 0, description: 'Below Average', colorCode: '#32CD32'},
  {grade: 'D', minPercentage: 70, maxPercentage: 84.99, bonusEntitlement: 0, description: 'Poor - Needs Improvement', colorCode: '#FFC107'},
  {grade: 'E', minPercentage: 60, maxPercentage: 69.99, bonusEntitlement: 0, description: 'Very Poor', colorCode: '#FFC107'},
  {grade: 'F', minPercentage: 0, maxPercentage: 59.99, bonusEntitlement: 0, description: 'Unacceptable', colorCode: '#DC3545'},
];

// Bonus Entitlement Thresholds (separate from grades)
export interface BonusThreshold {
  minPercentage: number;
  maxPercentage: number;
  bonusEntitlement: number;
}

export const BONUS_THRESHOLDS: BonusThreshold[] = [
  {minPercentage: 110, maxPercentage: 999, bonusEntitlement: 100},
  {minPercentage: 100.1, maxPercentage: 109.99, bonusEntitlement: 75},
  {minPercentage: 89.5, maxPercentage: 100, bonusEntitlement: 50},
  {minPercentage: 0, maxPercentage: 89.49, bonusEntitlement: 0},
];

// Helper function to get bonus entitlement from score
export const getBonusEntitlementFromScore = (score: number): number => {
  for (const threshold of BONUS_THRESHOLDS) {
    if (score >= threshold.minPercentage && score <= threshold.maxPercentage) {
      return threshold.bonusEntitlement;
    }
  }
  return 0;
};

// Unit of Measure Options
export const UNIT_OPTIONS = [
  { value: 'percentage', label: 'Percentage (%)' },
  { value: 'qualitative', label: 'Qualitative' },
  { value: 'compliance', label: 'Compliance (Yes/No)' },
  { value: 'currency', label: 'Currency ($)' },
  { value: 'date', label: 'Date' },
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'hours', label: 'Hours' },
  { value: 'number', label: 'Number' },
  { value: 'points', label: 'Points' },
  { value: 'ratio', label: 'Ratio' },
];

// Helper function to get grade from score
export const getGradeFromScore = (score: number): PerformanceRating | undefined => {
  return PERFORMANCE_RATINGS.find(
    rating => score >= rating.minPercentage && score <= rating.maxPercentage
  );
};

// Helper function to calculate KRA scores
export const calculateKRAScores = (perspectives: BSCPerspectiveScore[]): {
  perspectives: BSCPerspectiveScore[];
  totalWeightedScore: number;
  rawTotalWeightedScore?: number; // Uncapped raw score (only set if exceeded 110%)
  finalGrade: string;
  bonusEntitlement: number;
} => {
  let totalWeightedScore = 0;

  const MAX_KRA_PERFORMANCE = 110; // Cap individual KRA performance at 110%

  const calculatedPerspectives = perspectives.map(perspective => {
    let perspectiveTotal = 0;

    const calculatedKRAs = perspective.kraScores.map(kra => {
      // Calculate raw performance based on measurement direction
      let rawPerformance = 0;
      if (kra.actualValue !== undefined) {
        if (kra.measurementDirection === 'minimize') {
          // For 'minimize' metrics (costs, expenses, errors): lower actual is better
          if (kra.targetValue === 0 || kra.targetValue === undefined) {
            // When target is 0 (e.g., 0% error rate), use inverse linear formula
            // performance = max(0, 100 - actualValue)
            // Example: target=0%, actual=20% → performance=80%
            rawPerformance = Math.max(0, 100 - kra.actualValue);
          } else if (kra.actualValue > 0) {
            // Standard minimize formula: (target/actual) * 100
            rawPerformance = (kra.targetValue / kra.actualValue) * 100;
          } else {
            rawPerformance = 100; // If actual is 0, perfect performance for minimize
          }
        } else {
          // Default 'maximize' metrics (sales, revenue): higher actual is better
          if (kra.targetValue && kra.targetValue > 0) {
            rawPerformance = (kra.actualValue / kra.targetValue) * 100;
          } else {
            rawPerformance = 0;
          }
        }
      }

      // Cap KRA performance at 110% maximum
      const performance = Math.min(rawPerformance, MAX_KRA_PERFORMANCE);

      // Calculate score (capped performance * weight / 100)
      const score = (performance * kra.weight) / 100;

      // Calculate weighted score (perspective weight * score / 100)
      const weightedScore = (perspective.weight * score) / 100;

      perspectiveTotal += weightedScore;

      return {
        ...kra,
        performance,
        score,
        weightedScore,
      };
    });

    totalWeightedScore += perspectiveTotal;

    return {
      ...perspective,
      kraScores: calculatedKRAs,
      perspectiveTotal,
    };
  });

  // Get grade and bonus (using separate bonus thresholds)
  const rating = getGradeFromScore(totalWeightedScore);
  const finalGrade = rating?.grade || 'F';
  // Get bonus from separate bonus thresholds
  const bonusEntitlement = getBonusEntitlementFromScore(totalWeightedScore);

  // Cap performance at 110% maximum
  const MAX_PERFORMANCE = 110;
  const rawScore = totalWeightedScore;
  const cappedScore = Math.min(totalWeightedScore, MAX_PERFORMANCE);

  return {
    perspectives: calculatedPerspectives,
    totalWeightedScore: cappedScore,
    rawTotalWeightedScore: rawScore > MAX_PERFORMANCE ? rawScore : undefined,
    finalGrade,
    bonusEntitlement,
  };
};
